import React from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiCheck, FiExternalLink } from 'react-icons/fi';

import { HeaderNav, Section, Reveal, Button } from '../../shared';
import { getIcon } from '../../shared/icons/iconMap';
import { useSettings } from '../../../context/SettingsContext';

import './HomePage.scss';

export function HomePage() {
  const { t } = useSettings();
  const h = t.home;

  return (
    <>
      <HeaderNav />
      <main id="main">
        {/* ---------------- Hero ---------------- */}
        <header className="hero">
          <div className="hero__glow" aria-hidden="true" />
          <div className="ds-container hero__inner">
            <Reveal>
              <span className="ds-eyebrow">{h.eyebrow}</span>
            </Reveal>
            <Reveal delay={0.05}>
              <h1 className="hero__title">
                {h.titlePart1}
                <span className="ds-gradient-text">{h.titleAccent}</span>
                {h.titlePart2}
              </h1>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="ds-lead hero__lead">
                {h.leadPre}
                <strong>{h.leadStrong}</strong>
                {h.leadPost}
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="hero__actions">
                <Button to="/contact">{h.ctaPrimary}</Button>
                <Button to="/work" variant="secondary" withArrow={false}>
                  {h.ctaSecondary}
                </Button>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <ul className="hero__chips">
                {h.chips.map((chip) => (
                  <li key={chip}>
                    <FiCheck aria-hidden="true" /> {chip}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </header>

        {/* ---------------- Value / pricing highlight ---------------- */}
        <Section ariaLabel={h.value.title}>
          <div className="value">
            <Reveal className="value__card value__card--price">
              <span className="value__label">{h.value.from}</span>
              <span className="value__price">
                {h.value.price}
                <span>{h.value.unit}</span>
              </span>
              <p className="value__note">{h.value.priceNote}</p>
            </Reveal>
            <Reveal delay={0.08} className="value__vs">
              <div className="value__strike">
                <span className="value__label">{h.value.typicalStudio}</span>
                <span className="value__old">{h.value.oldPrice}</span>
                <p className="value__note">{h.value.oldNote}</p>
              </div>
              <div className="value__copy">
                <h2>{h.value.title}</h2>
                <p>{h.value.body}</p>
                <Button to="/services" variant="ghost">
                  {h.value.link}
                </Button>
              </div>
            </Reveal>
          </div>
        </Section>

        {/* ---------------- Process ---------------- */}
        <Section tint ariaLabel={h.processTitle}>
          <Reveal>
            <span className="ds-eyebrow">{h.processEyebrow}</span>
            <h2 className="section-title">{h.processTitle}</h2>
            <p className="ds-lead">{h.processLead}</p>
          </Reveal>
          <ol className="process">
            {t.process.map((step, i) => (
              <Reveal as="li" key={step.title} delay={i * 0.08} className="process__step ds-card">
                <span className="process__num">{i + 1}</span>
                <span className="process__icon">{getIcon(step.icon)}</span>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </Reveal>
            ))}
          </ol>
        </Section>

        {/* ---------------- Portfolio teaser ---------------- */}
        <Section ariaLabel={h.teaser.title}>
          <div className="teaser">
            <Reveal className="teaser__media">
              {t.caseStudies[0].image ? (
                <img src={t.caseStudies[0].image} alt={h.teaser.title} loading="lazy" />
              ) : (
                <div className="teaser__frame" role="img" aria-label={`${h.teaser.title} ${t.work.screenshotSoon}`}>
                  <span className="teaser__frame-tag">{h.teaser.title}</span>
                  <span className="teaser__frame-note">{t.work.screenshotSoon}</span>
                </div>
              )}
            </Reveal>
            <Reveal delay={0.1} className="teaser__body">
              <span className="ds-eyebrow">{h.teaser.eyebrow}</span>
              <h2>{h.teaser.title}</h2>
              <p>{h.teaser.body}</p>
              <div className="teaser__actions">
                <Button to="/work">{h.teaser.cta}</Button>
                <a className="teaser__live" href={t.caseStudies[0].url} target="_blank" rel="noopener noreferrer">
                  {h.teaser.live} <FiExternalLink aria-hidden="true" />
                </a>
              </div>
            </Reveal>
          </div>
        </Section>

        {/* ---------------- Why me / trust ---------------- */}
        <Section tint ariaLabel={h.trustTitle}>
          <Reveal>
            <span className="ds-eyebrow">{h.trustEyebrow}</span>
            <h2 className="section-title">{h.trustTitle}</h2>
          </Reveal>
          <div className="trust">
            {t.trust.map((point, i) => (
              <Reveal key={point.title} delay={i * 0.07} className="trust__item ds-card ds-card--hover">
                <span className="trust__icon">{getIcon(point.icon)}</span>
                <div>
                  <h3>{point.title}</h3>
                  <p>{point.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Section>

        {/* ---------------- Closing CTA ---------------- */}
        <Section ariaLabel={h.cta.title}>
          <Reveal className="cta">
            <div className="cta__glow" aria-hidden="true" />
            <h2>{h.cta.title}</h2>
            <p>{h.cta.body}</p>
            <div className="cta__actions">
              <Button to="/contact">{h.cta.primary}</Button>
              <Link to="/services" className="cta__link">
                {h.cta.link} <FiArrowRight aria-hidden="true" />
              </Link>
            </div>
          </Reveal>
        </Section>
      </main>
    </>
  );
}

export default HomePage;
